import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { PoolCard } from "@/components/teacher/PoolCard";
import { PoolNameDialog } from "@/components/teacher/PoolNameDialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { EMPTY_FILTER, ListFilter, Pager, useFiltered, usePaged } from "@/components/teacher/ListFilter";
import { ShareDialog } from "@/components/teacher/ShareDialog";
import { saveTags, TagDialog } from "@/components/teacher/Social";
import { PacksSection } from "@/components/teacher/PacksSection";
import { Showcase } from "@/components/teacher/Showcase";
import { useNavigate } from "react-router-dom";

const poolText = (p) => `${p.name} ${p.description || ""}`;

export default function PoolsPage() {
  const [pools, setPools] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [filter, setFilter] = useState(EMPTY_FILTER);
  const [sharing, setSharing] = useState(null);
  const [tagging, setTagging] = useState(null);
  const [nameDialog, setNameDialog] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/pools", { params: { include_archived: showArchived } }).then((r) => setPools(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [showArchived]);

  useEffect(() => { load(); }, [load]);

  const visible = useFiltered(pools, filter, poolText);
  const pg = usePaged(visible, 6);

  const act = (fn, msg) => async (...args) => {
    try { await fn(...args); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const handlers = {
    onRename: (p) => setNameDialog({ pool: p }),
    onDuplicate: act((p) => api.post(`/pools/${p.id}/duplicate`), "Havuz çoğaltıldı"),
    onArchive: act((p) => api.patch(`/pools/${p.id}`, { archived: !p.archived }), "Havuz güncellendi"),
    onDelete: (p) => setToDelete(p),
    onShare: (p) => setSharing(p),
    onTag: (p) => setTagging(p),
  };
  const saveName = act(async (name, tags) => {
    if (nameDialog.pool) return api.patch(`/pools/${nameDialog.pool.id}`, { name });
    const res = await api.post("/pools", { name });
    await saveTags("pools", res.data.id, tags);
    return res;
  }, "Kaydedildi");

  return (
    <div className="space-y-8">
      <div className="flex flex-col lg:flex-row lg:items-end gap-4">
        <div className="flex-1">
          <p className="eyebrow">İçerik yönetimi</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black mt-1">Soru Havuzları</h1>
        </div>
        <Button onClick={() => setNameDialog({ pool: null })} className="h-12 px-6 text-base font-black" data-testid="create-pool-button">
          <Plus className="h-5 w-5 mr-2" /> Yeni havuz
        </Button>
      </div>
      <div className="flex flex-col xl:flex-row gap-4 xl:items-center">
        <div className="flex-1"><ListFilter items={pools} value={filter} onChange={setFilter} placeholder="Havuz ya da öğretmen ara…" /></div>
        <div className="flex items-center gap-2">
          <Switch id="show-archived" checked={showArchived} onCheckedChange={setShowArchived} data-testid="show-archived-switch" />
          <Label htmlFor="show-archived">Arşivdekileri göster</Label>
        </div>
      </div>
      {visible.length === 0 ? (
        <div className="panel p-10 text-slate-400" data-testid="pools-empty">{pools.length ? "Aramaya uygun havuz yok." : "Henüz havuz yok. \"Yeni havuz\" ile başlayın."}</div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="pools-grid">
          {pg.items.map((p) => <PoolCard key={p.id} pool={p} {...handlers} onReload={load} />)}
        </div>
      )}
      <Pager pg={pg} testId="pools-pager" />
      <Showcase kind="pools" items={pools} onOpen={(p) => navigate(`/ogretmen/havuzlar/${p.id}`)} />
      <PacksSection kind="pools" items={pools} onCopied={load} onEditContent={(p) => navigate(`/ogretmen/havuzlar/${p.item_id}`)} />
      <ShareDialog kind="pools" item={sharing} onOpenChange={(o) => !o && setSharing(null)} onSaved={load} />
      <TagDialog kind="pools" item={tagging} onOpenChange={(o) => !o && setTagging(null)} onSaved={load} />
      <PoolNameDialog open={Boolean(nameDialog)} onOpenChange={(o) => !o && setNameDialog(null)}
        title={nameDialog?.pool ? "Havuzu yeniden adlandır" : "Yeni havuz"} initial={nameDialog?.pool?.name || ""} onSubmit={saveName} withTags={!nameDialog?.pool} />
      <ConfirmDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)} title="Havuz silinsin mi?"
        description={`"${toDelete?.name}" ve içindeki ${toDelete?.question_count ?? 0} soru ile bu havuzun sınıf rekorları kalıcı olarak silinecek. Oyun analizleri korunur.`}
        onConfirm={() => { act((p) => api.delete(`/pools/${p.id}`), "Havuz silindi")(toDelete); setToDelete(null); }} />
    </div>
  );
}
